/**
 * ToolManager - 도구 관리자
 */

import { drawTool } from "./DrawTool.js";
import { selectTool } from "./SelectTool.js";
import { featureEditTool } from "./FeatureEditTool.js";
import { measureTool } from "./MeasureTool.js";
import { labelTool } from "./LabelTool.js";
import { routingTool } from "./RoutingTool.js";
import { isochroneTool } from "./IsochroneTool.js";
import { eventBus, Events } from "../utils/EventBus.js";

class ToolManager {
  constructor() {
    this.currentTool = null;
    this.tools = {
      select: selectTool,
      "draw-point": drawTool,
      "draw-line": drawTool,
      "draw-polygon": drawTool,
      "draw-multipoint": drawTool,
      "draw-multiline": drawTool,
      "draw-multipolygon": drawTool,
      "measure-distance": measureTool,
      "measure-area": measureTool,
      "edit-split": featureEditTool
    };

    // 새 프로젝트/프로젝트 불러오기 시, 레이어가 아닌 도구 오버레이도 정리
    // (측정 도형·툴팁, 라벨 편집, 경로/등시선 마커, 그리기 임시 도형 등은
    //  layerManager를 거치지 않아 레이어 삭제만으로는 지워지지 않음)
    eventBus.on(Events.PROJECT_NEW, () => this.resetForNewProject());
  }

  /**
   * layerManager에 등록되지 않은 도구 오버레이를 모두 정리한다.
   */
  resetForNewProject() {
    this.deactivateCurrentTool();

    const safeCall = (fn) => {
      try { fn(); } catch (e) { console.warn('도구 정리 중 오류:', e); }
    };

    safeCall(() => measureTool && measureTool.clearMeasurements());
    safeCall(() => selectTool && selectTool.clearSelection && selectTool.clearSelection());
    safeCall(() => labelTool && labelTool.stopEditMode && labelTool.stopEditMode());
    safeCall(() => routingTool && routingTool.clear && routingTool.clear());
    safeCall(() => isochroneTool && isochroneTool.clear && isochroneTool.clear());

    this.updateToolbarUI(null);
    eventBus.emit(Events.TOOL_CHANGED, { tool: null });
  }

  activateTool(toolName) {
    this.deactivateCurrentTool();

    switch (toolName) {
      case "select":
        selectTool.activate();
        break;
      case "draw-point":
        drawTool.activate("Point");
        break;
      case "draw-line":
        drawTool.activate("LineString");
        break;
      case "draw-polygon":
        drawTool.activate("Polygon");
        break;
      case "draw-multipoint":
        drawTool.activate("MultiPoint");
        break;
      case "draw-multiline":
        drawTool.activate("MultiLineString");
        break;
      case "draw-multipolygon":
        drawTool.activate("MultiPolygon");
        break;
      case "measure-distance":
        measureTool.activate("distance");
        break;
      case "measure-area":
        measureTool.activate("area");
        break;
      case "edit-split":
        featureEditTool.startSplit();
        break;
      case "pan":
        break;
      default:
        console.warn("Unknown tool:", toolName);
        return;
    }

    this.currentTool = toolName;
    this.updateToolbarUI(toolName);
    eventBus.emit(Events.TOOL_CHANGED, { tool: toolName });
  }

  deactivateCurrentTool() {
    if (drawTool.getIsActive()) {
      drawTool.deactivate();
    }
    if (selectTool.getIsActive()) {
      selectTool.deactivate();
    }
    if (measureTool.getIsActive()) {
      measureTool.deactivate();
    }
    if (featureEditTool.getIsActive()) {
      featureEditTool.deactivate();
    }
    this.currentTool = null;
  }

  updateToolbarUI(activeTool) {
    document.querySelectorAll("#toolbar .btn-icon").forEach(function(btn) {
      btn.classList.remove("active");
    });

    var activeBtn = document.querySelector("#toolbar [data-tool=\"" + activeTool + "\"]");
    if (activeBtn) {
      activeBtn.classList.add("active");
    }
  }

  getCurrentTool() {
    return this.currentTool;
  }

  toggleTool(toolName) {
    if (this.currentTool === toolName) {
      // 멀티 도형 그리기 중이면 저장
      if (drawTool.getIsMultiMode() && drawTool.getMultiFeatureCount() > 0) {
        drawTool.saveMultiFeature();
      }
      this.deactivateCurrentTool();
      this.updateToolbarUI(null);
      eventBus.emit(Events.TOOL_CHANGED, { tool: null });
    } else {
      this.activateTool(toolName);
    }
  }

  deleteSelectedFeatures() {
    if (selectTool && selectTool.getSelectedFeatures && selectTool.getSelectedFeatures().length === 0) {
      return false;
    }
    if (selectTool && selectTool.deleteSelected) {
      // 확인창에서 취소하면 false 가 반환되어 "삭제됨" 메시지가 뜨지 않는다.
      return selectTool.deleteSelected();
    }
    return false;
  }

  selectAllFeatures() {
    if (selectTool && selectTool.selectAll) {
      selectTool.selectAll();
    }
  }

  clearSelection() {
    if (selectTool && selectTool.clearSelection) {
      selectTool.clearSelection();
    }
  }

  clearMeasurements() {
    if (measureTool) {
      measureTool.clearMeasurements();
    }
  }
}

export var toolManager = new ToolManager();
